<script setup>
/**
 * CanDo — Slot-based permission/role gate component.
 *
 * Props:
 *   permission   {string|string[]}  — require ANY one of these permissions
 *   all          {string[]}         — require ALL of these permissions
 *   role         {string|string[]}  — require ANY one of these roles
 *   fallback     {boolean}          — if true, render the #fallback slot when denied
 *
 * Slots:
 *   default   — rendered when the user IS authorised
 *   fallback  — rendered when the user IS NOT authorised (only when :fallback="true")
 *
 * Examples:
 *
 *   <!-- Hide create button if user lacks permission -->
 *   <CanDo permission="projects.create">
 *     <Button label="New Project" />
 *   </CanDo>
 *
 *   <!-- Show a read-only notice when user cannot edit -->
 *   <CanDo permission="projects.update" :fallback="true">
 *     <template #default>  <Button label="Edit" />  </template>
 *     <template #fallback> <Tag value="Read-only" severity="secondary" /> </template>
 *   </CanDo>
 *
 *   <!-- Require ALL permissions -->
 *   <CanDo :all="['projects.update', 'projects.approve']">
 *     <Button label="Approve & Save" />
 *   </CanDo>
 *
 *   <!-- Role gate -->
 *   <CanDo :role="['PMO Manager', 'Admin']">
 *     <AdminPanel />
 *   </CanDo>
 */

import { computed } from 'vue'
import { usePermission } from '@/composables/usePermission'

const props = defineProps({
  /** Require ANY one of these permissions */
  permission: {
    type: [String, Array],
    default: null,
  },
  /** Require ALL of these permissions */
  all: {
    type: Array,
    default: null,
  },
  /** Require ANY one of these roles */
  role: {
    type: [String, Array],
    default: null,
  },
  /** When true, the #fallback slot is rendered for unauthorised users */
  fallback: {
    type: Boolean,
    default: false,
  },
})

const { can, canAny, canAll, hasAnyRole, isAdmin } = usePermission()

const isAuthorised = computed(() => {
  // Super Admin / Admin bypass all checks
  if (isAdmin.value) return true

  // Permission check (any)
  if (props.permission) {
    const perms = Array.isArray(props.permission) ? props.permission : [props.permission]
    if (!canAny(perms)) return false
  }

  // Permission check (all)
  if (props.all && props.all.length) {
    if (!canAll(props.all)) return false
  }

  // Role check
  if (props.role) {
    const roles = Array.isArray(props.role) ? props.role : [props.role]
    if (!hasAnyRole(roles)) return false
  }

  return true
})
</script>

<template>
  <slot v-if="isAuthorised" />
  <slot v-else-if="fallback" name="fallback" />
</template>
